import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api, photoUrl } from "../lib/api";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Activity, Flame, ImageIcon, PlusCircle, Camera, Pill, Stethoscope, X as XIcon } from "lucide-react";
import PillMultiSelect from "../components/PillMultiSelect";
import AddTreatmentDialog from "../components/AddTreatmentDialog";
import { toast } from "sonner";

const CONDITIONS = [
  "Atopic dermatitis",
  "Contact dermatitis",
  "Irritant dermatitis",
  "Psoriasis",
  "Acne",
  "Hidradenitis suppurativa",
  "Drug/viral rash",
  "Fungal infection",
  "Topical Steroid Withdrawal Syndrome",
  "Diagnosis uncertain",
];

const RX_TREATMENTS = [
  "Topical corticosteroid (low potency)",
  "Topical corticosteroid (high potency)",
  "Topical calcineurin inhibitor (Tacrolimus / Pimecrolimus)",
  "Topical retinoid (Tretinoin / Adapalene)",
  "Topical antibiotic (Clindamycin / Erythromycin)",
  "Oral antibiotic (Doxycycline / Minocycline)",
  "Oral antihistamine (Rx strength)",
  "Oral isotretinoin",
  "Oral antifungal",
  "Biologic injection (Dupilumab / IL-17 / TNF-a)",
  "JAK inhibitor",
  "Phototherapy / UVB",
];

const OTC_TREATMENTS = [
  "Emollient / moisturizer",
  "Petrolatum (Vaseline / Aquaphor)",
  "Ceramide cream",
  "Colloidal oatmeal bath",
  "Hydrocortisone 1% (OTC)",
  "Salicylic acid",
  "Benzoyl peroxide",
  "Sunscreen SPF 30+",
  "Gentle / soap-free cleanser",
  "OTC antihistamine (Cetirizine / Loratadine)",
  "Antifungal cream (OTC)",
  "Hydrocolloid patch",
  "Cold compress",
];

function dateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function overallSeverity(e) {
  const enabled = e.enabled_symptoms || ["itching", "pain", "redness"];
  const vals = enabled.map((s) => e[s] || 0);
  if (vals.length === 0) return 0;
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [rx, setRx] = useState([]);
  const [otc, setOtc] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, e, c, t] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/entries"),
          api.get("/conditions"),
          api.get("/treatments-list"),
        ]);
        setStats(s.data); setEntries(e.data || []);
        setConditions(c.data?.conditions || []);
        setRx(t.data?.prescribed || []);
        setOtc(t.data?.non_prescribed || []);
      } catch (err) { console.error(err); }
    })();
  }, []);

  const toggleCondition = async (c) => {
    const next = conditions.includes(c) ? conditions.filter(x => x !== c) : [...conditions, c];
    setConditions(next);
    try { await api.put("/conditions", { conditions: next }); }
    catch { setConditions(conditions); }
  };

  const toggleRx = async (item) => {
    // item can be an object {name, category} (new) or string (legacy)
    const isSame = (a, b) => {
      const an = typeof a === "string" ? a : a?.name;
      const bn = typeof b === "string" ? b : b?.name;
      return an === bn;
    };
    const exists = rx.some((x) => isSame(x, item));
    const next = exists ? rx.filter((x) => !isSame(x, item)) : [...rx, item];
    setRx(next);
    try { await api.put("/treatments-list", { prescribed: next, non_prescribed: otc }); }
    catch { setRx(rx); }
  };

  const addRx = (entry) => {
    const next = [...rx, entry];
    setRx(next);
    api.put("/treatments-list", { prescribed: next, non_prescribed: otc })
       .catch((e) => {
         setRx(rx);
         const d = e.response?.data?.detail;
         toast.error(typeof d === "string" ? d : "Could not save medication");
       });
  };

  const removeRxAt = (idx) => {
    const next = rx.filter((_, i) => i !== idx);
    setRx(next);
    api.put("/treatments-list", { prescribed: next, non_prescribed: otc })
       .catch(() => setRx(rx));
  };

  const addOtc = (entry) => {
    const next = [...otc, entry];
    setOtc(next);
    api.put("/treatments-list", { prescribed: rx, non_prescribed: next })
       .catch((e) => {
         setOtc(otc);
         const d = e.response?.data?.detail;
         toast.error(typeof d === "string" ? d : "Could not save item");
       });
  };

  const removeOtcAt = (idx) => {
    const next = otc.filter((_, i) => i !== idx);
    setOtc(next);
    api.put("/treatments-list", { prescribed: rx, non_prescribed: next })
       .catch(() => setOtc(otc));
  };

  const toggleOtc = async (item) => {
    const isSame = (a, b) => {
      const an = typeof a === "string" ? a : a?.name;
      const bn = typeof b === "string" ? b : b?.name;
      return an === bn;
    };
    const exists = otc.some((x) => isSame(x, item));
    const next = exists ? otc.filter((x) => !isSame(x, item)) : [...otc, item];
    setOtc(next);
    try { await api.put("/treatments-list", { prescribed: rx, non_prescribed: next }); }
    catch { setOtc(otc); }
  };

  const entryByDate = useMemo(() => {
    const m = {};
    for (const e of entries) m[e.entry_date] = e;
    return m;
  }, [entries]);

  const loggedDates = useMemo(
    () => entries.map((e) => new Date(e.entry_date + "T00:00")),
    [entries]
  );

  const onDayClick = (date) => {
    const key = dateKey(date);
    const entry = entryByDate[key];
    if (entry) navigate(`/entry/${entry.id}`);
    else navigate(`/new?date=${key}`);
  };

  const DayContent = ({ date }) => {
    const key = dateKey(date);
    const entry = entryByDate[key];
    const hasPhotos = (entry?.photo_paths?.length ?? 0) > 0;
    const hasTreatments = (entry?.treatments?.length ?? 0) > 0;
    return (
      <div className="relative h-full w-full flex items-center justify-center" data-testid={`cal-day-${key}`}>
        <span className="text-sm">{date.getDate()}</span>
        {(hasPhotos || hasTreatments) && (
          <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 flex gap-[2px]" data-testid={`cal-day-${key}-icons`}>
            {hasPhotos && <Camera className="w-[9px] h-[9px]" data-testid={`cal-day-${key}-photo-icon`} />}
            {hasTreatments && <Pill className="w-[9px] h-[9px]" data-testid={`cal-day-${key}-treatment-icon`} />}
          </div>
        )}
      </div>
    );
  };

  return (
    <AppShell>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-sage-500">Your journal</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-stone-900 mt-2">Dashboard</h1>
        </div>
        <Link to="/new"><Button className="rounded-full bg-sage-400 hover:bg-sage-500 text-white" data-testid="dashboard-new-entry-btn"><PlusCircle className="w-4 h-4 mr-2"/>New entry</Button></Link>
      </div>

      <section className="mt-8">
        <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="conditions-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Stethoscope className="w-4 h-4 text-sage-500"/>
              <h3 className="font-serif text-2xl text-stone-900">Diagnosed conditions</h3>
            </div>
            <p className="text-[11px] uppercase tracking-widest text-stone-400">Shared on your dermatologist PDF</p>
          </div>

          <div className="mt-4">
            <PillMultiSelect
              options={CONDITIONS}
              selected={conditions}
              onToggle={toggleCondition}
              addLabel="Add a diagnosis"
              addLabelMore="Add another"
              testidPrefix="condition"
            />
          </div>
          {conditions.length === 0 && (
            <p className="mt-3 text-xs text-stone-400 italic">No diagnoses recorded yet.</p>
          )}
        </div>
      </section>

      <section className="mt-6">
        <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="treatments-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Pill className="w-4 h-4 text-sage-500"/>
              <h3 className="font-serif text-2xl text-stone-900">Treatments</h3>
            </div>
            <p className="text-[11px] uppercase tracking-widest text-stone-400">Ongoing regimen · saved to PDF</p>
          </div>

          <div className="mt-5 grid md:grid-cols-2 gap-6 divide-y md:divide-y-0 md:divide-x divide-stone-100">
            <div className="md:pr-6" data-testid="treatments-rx">
              <p className="text-xs uppercase tracking-[0.18em] text-sage-500 mb-3">Prescribed</p>
              <div className="flex flex-wrap gap-2 items-center" data-testid="rx-picker">
                {rx.map((it, i) => {
                  const name = typeof it === "string" ? it : (it?.name || "");
                  const category = typeof it === "string" ? "Other" : (it?.category || "Other");
                  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                  return (
                    <span key={`${name}-${i}`} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-sm bg-sage-500 text-white" data-testid={`rx-pill-${slug}`}>
                      <span className="flex flex-col leading-tight">
                        <span>{name}</span>
                        {category && category !== "Other" && (
                          <span className="text-[10px] uppercase tracking-wider opacity-75">{category}</span>
                        )}
                      </span>
                      <button onClick={() => removeRxAt(i)} className="rounded-full hover:bg-black/10 p-0.5 transition-colors" data-testid={`rx-pill-${slug}-remove`} aria-label={`Remove ${name}`}>
                        <XIcon className="w-3 h-3"/>
                      </button>
                    </span>
                  );
                })}
                <AddTreatmentDialog onAdd={addRx} hasItems={rx.length > 0} />
              </div>
              {rx.length === 0 && <p className="mt-3 text-xs text-stone-400 italic">No prescriptions logged.</p>}
            </div>
            <div className="md:pl-6 pt-6 md:pt-0" data-testid="treatments-otc">
              <p className="text-xs uppercase tracking-[0.18em] text-clay-500 mb-3">Non-prescribed</p>
              <div className="flex flex-wrap gap-2 items-center" data-testid="otc-picker">
                {otc.map((it, i) => {
                  const name = typeof it === "string" ? it : (it?.name || "");
                  const category = typeof it === "string" ? "Other" : (it?.category || "Other");
                  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                  return (
                    <span key={`${name}-${i}`} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-sm bg-clay-500 text-white" data-testid={`otc-pill-${slug}`}>
                      <span className="flex flex-col leading-tight">
                        <span>{name}</span>
                        {category && category !== "Other" && (
                          <span className="text-[10px] uppercase tracking-wider opacity-75">{category}</span>
                        )}
                      </span>
                      <button onClick={() => removeOtcAt(i)} className="rounded-full hover:bg-black/10 p-0.5 transition-colors" data-testid={`otc-pill-${slug}-remove`} aria-label={`Remove ${name}`}>
                        <XIcon className="w-3 h-3"/>
                      </button>
                    </span>
                  );
                })}
                <AddTreatmentDialog
                  onAdd={addOtc}
                  title="Add a non-prescribed item"
                  triggerLabel="Add an OTC item"
                  triggerLabelMore="Add another"
                  hasItems={otc.length > 0}
                  testidPrefix="otc"
                  triggerClass="border-clay-400 text-clay-500 hover:bg-emerald-50"
                  primaryClass="bg-clay-500 hover:bg-clay-400 text-white"
                  activeTabClass="data-[state=active]:bg-clay-500 data-[state=active]:text-white"
                  scanCardClass="border-clay-400/40 bg-emerald-50 hover:bg-emerald-100"
                  scanIconClass="text-clay-500"
                  scanTextClass="text-clay-500"
                />
              </div>
              {otc.length === 0 && <p className="mt-3 text-xs text-stone-400 italic">No OTC items logged.</p>}
            </div>
          </div>
        </div>
      </section>

      <DashboardCarousel
        loggedDates={loggedDates}
        onDayClick={onDayClick}
        DayContent={DayContent}
        stats={stats}
      />

      <section className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
        <StatCard label="Entries logged" value={stats?.total_entries ?? 0} icon={ImageIcon} testid="stat-total"/>
        <StatCard label="Avg severity" value={stats?.avg_severity ?? 0} suffix="/10" icon={Activity} testid="stat-severity"/>
        <StreakCard
          current={stats?.current_streak ?? 0}
          longest={stats?.longest_streak ?? 0}
          week={stats?.entries_last_7_days ?? 0}
        />
      </section>
    </AppShell>
  );
}

function StatCard({ label, value, suffix, icon: Icon, testid }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.18em] text-stone-500">{label}</p>
        <Icon className="w-4 h-4 text-sage-400"/>
      </div>
      <p className="font-serif text-5xl text-stone-900 mt-3">{value}<span className="text-2xl text-stone-400">{suffix}</span></p>
    </div>
  );
}

function StreakCard({ current, longest, week }) {
  const active = current > 0;
  const isPersonalBest = active && current === longest && current > 1;
  const subline = current === 0
    ? "Log today to start a streak"
    : isPersonalBest
      ? "Personal best — keep going!"
      : longest > current
        ? `Best: ${longest} days`
        : "Keep it going!";
  return (
    <div
      className={`relative overflow-hidden rounded-2xl p-6 border transition-colors ${active ? "bg-gradient-to-br from-[#fff5ec] via-white to-[#fff9f1] border-[#fbd9b6]" : "bg-white border-stone-100"}`}
      data-testid="stat-streak"
    >
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.18em] text-stone-500">Current streak</p>
        <Flame
          className={`w-5 h-5 ${active ? "streak-flame-pulse" : "text-stone-300"}`}
          style={active ? { color: "#e07b3a" } : undefined}
          data-testid="streak-flame"
        />
      </div>
      <p className="font-serif text-5xl text-stone-900 mt-3" data-testid="streak-value">
        {current}<span className="text-2xl text-stone-400"> {current === 1 ? "day" : "days"}</span>
      </p>
      <p className={`mt-1 text-xs ${active ? "" : "text-stone-400 italic"}`} style={active ? { color: "#b85f29" } : undefined} data-testid="streak-subline">
        {subline}
      </p>

      {/* Last 7 days dot tracker */}
      <div className="mt-4 flex items-center gap-1.5" data-testid="streak-week-dots" aria-label={`${week} of last 7 days logged`}>
        {Array.from({ length: 7 }).map((_, i) => {
          const filled = i < week;
          return (
            <span
              key={i}
              data-testid={`streak-week-dot-${i}${filled ? "-filled" : ""}`}
              className="h-2 flex-1 rounded-full transition-colors"
              style={{ backgroundColor: filled ? "#e07b3a" : "#e7e5e4" }}
            />
          );
        })}
      </div>
      <p className="mt-2 text-[10px] uppercase tracking-widest text-stone-400">
        {week}/7 days this week
      </p>
    </div>
  );
}

function Dot({ color, label }) {
  return <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: color }}/>{label}</span>;
}

function Legend() {
  return (
    <span className="text-[11px] uppercase tracking-widest text-stone-400">Tap a day</span>
  );
}

function DashboardCarousel({ loggedDates, onDayClick, DayContent, stats }) {
  const scrollerRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Update active dot as user scrolls (mobile only)
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const slideWidth = el.clientWidth;
      if (slideWidth === 0) return;
      const idx = Math.round(el.scrollLeft / slideWidth);
      setActiveSlide(idx);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (i) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  const calendarCard = (
    <div className="bg-white rounded-2xl p-4 sm:p-6 border border-stone-100" data-testid="dashboard-calendar-card">
      <div className="flex items-center justify-between px-2 pt-1">
        <h3 className="font-serif text-2xl text-stone-900">Calendar</h3>
        <Legend />
      </div>
      <Calendar
        mode="single"
        onDayClick={onDayClick}
        modifiers={{ logged: loggedDates }}
        modifiersClassNames={{
          logged: "bg-sage-500 text-white hover:bg-sage-600 hover:text-white rounded-md font-semibold",
        }}
        components={{ DayContent }}
        className="mt-2"
        classNames={{ day: "h-9 w-9 p-0 font-normal cursor-pointer relative" }}
        data-testid="dashboard-calendar"
      />
      <div className="mt-3 px-2 flex items-center justify-between text-[11px] text-stone-500">
        <span className="flex items-center gap-1.5"><Camera className="w-3 h-3" />photos</span>
        <span className="flex items-center gap-1.5"><Pill className="w-3 h-3" />topicals</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-sage-500"/>logged</span>
      </div>
    </div>
  );

  const trendCard = (
    <div className="bg-white rounded-2xl p-6 border border-stone-100" data-testid="trend-chart-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-serif text-2xl text-stone-900">Severity over time</h3>
        <div className="flex gap-3 text-xs text-stone-500 flex-wrap">
          <Dot color="#3FB54A" label="Itching"/>
          <Dot color="#14B881" label="Pain"/>
          <Dot color="#B45757" label="Redness"/>
          <Dot color="#C9A86A" label="Dryness"/>
          <Dot color="#5B8AD9" label="Weeping"/>
        </div>
      </div>
      <div className="h-64 mt-6">
        {(stats?.trend?.length ?? 0) === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-stone-400 text-sm" data-testid="empty-trend">
            <p>No entries yet — log your first day to see your trend appear here.</p>
            <Link to="/new" className="mt-3 text-sage-500 underline text-sm">Start now</Link>
          </div>
        ) : (
          <ResponsiveContainer>
            <LineChart data={stats?.trend || []}>
              <CartesianGrid stroke="#E7EDE3" strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6F7A6A" }} stroke="#D4DCCD"/>
              <YAxis domain={[0, 10]} tick={{ fontSize: 11, fill: "#6F7A6A" }} stroke="#D4DCCD"/>
              <Tooltip contentStyle={{ background: "rgba(255,255,255,0.95)", border: "1px solid #E2E9DD", borderRadius: 12 }} />
              <Line type="monotone" dataKey="itching" stroke="#3FB54A" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="pain" stroke="#14B881" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="redness" stroke="#B45757" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );

  return (
    <section className="mt-8" data-testid="dashboard-carousel-section">
      {/* Desktop: side-by-side grid (lg and up) */}
      <div className="hidden lg:grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">{calendarCard}</div>
        <div className="lg:col-span-2">{trendCard}</div>
      </div>

      {/* Mobile/tablet: swipeable carousel */}
      <div className="lg:hidden">
        <div
          ref={scrollerRef}
          className="flex overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-1 hide-scrollbar"
          data-testid="dashboard-mobile-carousel"
          style={{ scrollbarWidth: "none" }}
        >
          <div className="snap-center shrink-0 w-full pr-3" data-testid="carousel-slide-calendar">
            {calendarCard}
          </div>
          <div className="snap-center shrink-0 w-full pl-3" data-testid="carousel-slide-trend">
            {trendCard}
          </div>
        </div>
        <div className="mt-3 flex items-center justify-center gap-2" data-testid="carousel-dots">
          {[0, 1].map((i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              aria-label={i === 0 ? "Show calendar" : "Show severity chart"}
              data-testid={`carousel-dot-${i}${activeSlide === i ? "-active" : ""}`}
              className={`h-2 rounded-full transition-all ${activeSlide === i ? "w-6 bg-sage-500" : "w-2 bg-stone-300"}`}
            />
          ))}
        </div>
        <p className="mt-2 text-center text-[11px] uppercase tracking-widest text-stone-400">
          {activeSlide === 0 ? "Swipe for trend →" : "← Swipe for calendar"}
        </p>
      </div>
    </section>
  );
}
